import { useMemo, useCallback, useEffect } from 'react';

import { useForm } from 'react-hook-form';
import { StylesConfig } from 'react-select';

import { Select, SelectProps } from 'components/ui/forms';
import { TableProps } from 'components/ui/table';
import { useTable } from 'hooks';

/** Keep filter option menus readable when the trigger is compact. */
const FILTER_SELECT_STYLES: StylesConfig = {
  menu: (base) => ({
    ...base,
    minWidth: '260px',
  }),
  option: (base) => ({
    ...base,
    whiteSpace: 'normal',
    overflowWrap: 'break-word',
    wordBreak: 'normal',
  }),
};

export type FilterSelectProps = {
  select: {
    name: SelectProps['name'];
    multiLabelWithNumber?: SelectProps['multiLabelWithNumber'];
    placeholder?: SelectProps['placeholder'];
    loading?: SelectProps['loading'];
    isClearable?: boolean;
    isSearchable?: SelectProps['isSearchable'];
    defaultOption?: {
      value: string;
      label: string;
    };
    options: {
      value: string;
      label: string;
    }[];
  };
  handleQueryParameters: TableProps['handleQueryParameters'];
  onFilterChange: (
    filter: FilterSelectProps['select']['options'],
    selectOptions: FilterSelectProps['select']['options']
  ) => void;
};

export const FilterSelect = ({
  handleQueryParameters,
  select,
  onFilterChange,
}: FilterSelectProps) => {
  const { getFilterQueryParameter } = useTable();
  const { control, setValue } = useForm<any>();
  const isMulti = !!select.multiLabelWithNumber;

  const defaultValue = useMemo(() => {
    const getFilter = getFilterQueryParameter();

    if (getFilter?.length && handleQueryParameters) {
      const fromUrl = getFilter
        .map((filter: any) =>
          select.options.find(({ value }) => value === filter)
        )
        .filter((filter: any) => filter);

      if (fromUrl.length) {
        return isMulti ? fromUrl : fromUrl[0];
      }
    }

    if (select.defaultOption) {
      return isMulti ? [select.defaultOption] : select.defaultOption;
    }

    return isMulti ? [] : undefined;
  }, [
    getFilterQueryParameter,
    select.options,
    select.defaultOption,
    handleQueryParameters,
    isMulti,
  ]);

  const handleOnChange = useCallback(
    (filter: any) => {
      const normalizedFilters = Array.isArray(filter)
        ? filter
        : filter?.value
        ? [filter]
        : [];

      onFilterChange(normalizedFilters, select.options);
    },
    [onFilterChange, select.options]
  );

  useEffect(() => {
    setValue(select.name, defaultValue ?? (isMulti ? [] : null));
  }, [defaultValue, select.name, setValue, isMulti]);

  return (
    <Select
      name={select.name}
      multiLabelWithNumber={select.multiLabelWithNumber}
      placeholder={select.placeholder || select.multiLabelWithNumber}
      loading={select.loading}
      options={select.options}
      control={control}
      fullWidth
      isClearable={select.isClearable ?? true}
      isSearchable={select.isSearchable ?? false}
      isMulti={isMulti}
      closeMenuOnSelect={!isMulti}
      hideSelectedOptions={false}
      onChange={handleOnChange}
      defaultValue={defaultValue}
      selectStyles={FILTER_SELECT_STYLES}
    />
  );
};
